import { Stack } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, Targeting } from '@feathr/blackbox';
import { CardV2 as Card, Checkbox } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import * as styles from './AgeDemographics.css';

function getAgeDemographicLabels(t: TFunction): Record<string, string> {
  return {
    '18-24': t('Young adults, college students'),
    '25-34': t('Young professionals, new families'),
    '35-44': t('Established careers, growing families'),
    '45-54': t('Peak earning years, community leaders'),
    '55-64': t('Pre-retirement, established donors'),
    '65+': t('Retirees, legacy supporters'),
  };
}

export function getAgeDemographicTargetings(targetings: IObservableArray<Targeting>): Targeting[] {
  return targetings.filter(
    (t) => t.get('kind') === 'age_demographic' && !!t.get('target_data') && !t.get('is_archived'),
  );
}

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
}

function AgeDemographics({ campaign, targetings }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Targetings, Targetables } = useStore();

  const availableDemographics = Targetables.getAgeDemographics().models;

  // Derive selected demographics directly from targetings
  const selectedDemographicIds = getAgeDemographicTargetings(targetings).map(
    (targeting) => targeting.get('target_data') as string,
  );

  const handleDemographicChange = useCallback(
    (checked: boolean | undefined, demographicId: string): void => {
      const isChecked = checked ?? false;
      const demographic = availableDemographics.find((d) => d.id === demographicId);

      if (!demographic) {
        return;
      }

      if (isChecked) {
        // Create new targeting
        const model = Targetings.create({
          parent: campaign.get('id'),
          kind: 'age_demographic',
          included: true,
        });
        runInAction(() => {
          targetings.push(model);
        });

        // Then populate it (this makes it dirty!)
        model.set({
          name: demographic.get('name'),
          // Add a unique group so the age targets are or'd together
          group: -2,
          target_data: demographic.id,
        });
      } else {
        // Remove existing targeting - only find non-archived targetings
        const existingTargeting = targetings.find((targeting) => {
          return (
            targeting.get('kind') === 'age_demographic' &&
            targeting.get('target_data') === demographicId &&
            !targeting.get('is_archived')
          );
        });

        if (existingTargeting) {
          if (!existingTargeting.isEphemeral) {
            // Archive the targeting lets it get set to dirty
            runInAction(() => {
              existingTargeting.set({ is_archived: true });
            });
          } else {
            runInAction(() => {
              targetings.remove(existingTargeting);
              Targetings.remove(existingTargeting.id);
            });
          }
        }
      }
    },
    [availableDemographics, campaign, targetings, Targetings],
  );

  const ageDemographicLabels = getAgeDemographicLabels(t);
  return (
    <Card width={'full'}>
      <Card.Header isCollapsible={true} title={t('Age Demographics')} />
      <Card.Content addVerticalGap={false} contentClassName={styles.root}>
        {availableDemographics.map((demographic) => {
          const description = ageDemographicLabels[demographic.get('name')];
          const label = t('{{ range }} years', { range: demographic.get('name') });
          return (
            <Checkbox
              key={demographic.id}
              label={
                <Stack gap={0}>
                  {label}
                  {description && <span className={styles.description}>{description}</span>}
                </Stack>
              }
              layout={'well'}
              name={'ageDemographics'}
              onChange={(checked): void => handleDemographicChange(checked, demographic.id)}
              value={selectedDemographicIds.includes(demographic.id)}
            />
          );
        })}
      </Card.Content>
    </Card>
  );
}

export default observer(AgeDemographics);
